import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import PageHeader from 'components/global/PageHeader';
import Tooltip from 'components/global/Tooltip';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useAppTracking from 'hooks/useAppTracking';
import useModalRouter from 'hooks/useModalRouter';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import { BRAND } from 'services/consts';

import { DYNAMICLISTS } from 'utils/constants';
import PATHS from 'utils/paths';

import { faArrowLeft, faEye, faPlus } from '@fortawesome/pro-solid-svg-icons';

const BrandList = () => {
	const { showModal } = useModalRouter();
	const isRestricted = useRestriction();
	const { track } = useAppTracking();
	const brandCount = useSelector((state) => state.dynamicListData[DYNAMICLISTS.BRANDS]?.size);
	const isAddButtonDisabled = isRestricted(SYSTEM_LIMITS.BRAND, brandCount);
	const navigate = useNavigate();
	const { state } = useLocation();

	const defaultSort = useMemo(() => [{ id: 'ID', desc: true, show: false }], []);
	const columns = useMemo(
		() => [
			{ Header: 'id', accessor: 'ID', filterType: FilterType.number, show: false },
			{
				Header: 'storeName',
				accessor: 'storeName',
				filterType: FilterType.string,
				renderCell: ListComponents.BRAND_LOGO,
				showOnMobile: true
			},
			{ Header: 'defaultWarehouse', accessor: 'warehouseName', filterType: FilterType.multiSelection }
		],
		[]
	);

	return (
		<div>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/brand-icon.json.gz"
				title="brands"
				moduleName="multi-brand"
				{...(state?.showBackButton
					? {
							showBackButton: true,
							subtitle: 'backToMultiBrandModule',
							icon: faArrowLeft,
							backUrl: PATHS.MULTI_BRAND_MODULE
						}
					: {})}>
				<Authorization path="/modal/brand-form">
					{isAddButtonDisabled ? (
						<Tooltip
							timeout={100}
							placement="bottom"
							color={'var(--white)'}
							title={(closePopper) => (
								<div style={{ display: 'block' }}>
									<ModuleToolTipContainer
										infoImage="lottie_multiBrand"
										title="multiBrandModule"
										description="multiBrandModuleSubtitle"
									/>

									<Button
										style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
										className="mt-3"
										label="showPlans"
										icon={faEye}
										variant="blue"
										onClick={() => {
											track('moreThan3Brands');
											navigate(PATHS.OTO_MODULES);
											closePopper();
										}}
									/>
								</div>
							)}>
							<Button
								icon={faPlus}
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
								id="MultiBrandInstruction1"
								label="addBrand"
								onClick={() =>
									showModal('brand-form', {
										mode: 'add'
									})
								}
								variant="blue"
								className="addButton"
								disabled={isAddButtonDisabled}
							/>
						</Tooltip>
					) : (
						<Button
							icon={faPlus}
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
							id="MultiBrandInstruction1"
							label="addBrand"
							onClick={() =>
								showModal('brand-form', {
									mode: 'add'
								})
							}
							variant="blue"
							className="addButton"
							disabled={isAddButtonDisabled}
						/>
					)}
				</Authorization>
			</PageHeader>

			<DynamicList
				id={DYNAMICLISTS.BRANDS}
				defaultSort={defaultSort}
				columns={columns}
				fetchUrl={BRAND.GET_BRAND_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="brands"
			/>
		</div>
	);
};

export default BrandList;
